import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiMapPin, FiChevronDown, FiChevronUp, FiArrowRight, FiLayers } from 'react-icons/fi';
import { fmtTimeMultiline } from '../utils/time';
import '../styles/LogTable.css';

const ACTION_COLORS = {
    player_connect:       { bg: '#4ade8020', text: '#4ade80', border: '#4ade8040' },
    player_disconnect:    { bg: '#f8717120', text: '#f87171', border: '#f8717140' },
    player_death:         { bg: '#ef444420', text: '#ef4444', border: '#ef444440' },
    player_kill:          { bg: '#dc262620', text: '#dc2626', border: '#dc262640' },
    vehicle_spawn:        { bg: '#60a5fa20', text: '#60a5fa', border: '#60a5fa40' },
    vehicle_delete:       { bg: '#3b82f620', text: '#3b82f6', border: '#3b82f640' },
    item_pickup:          { bg: '#a78bfa20', text: '#a78bfa', border: '#a78bfa40' },
    item_drop:            { bg: '#8b5cf620', text: '#8b5cf6', border: '#8b5cf640' },
    weapon_fire:          { bg: '#fb923c20', text: '#fb923c', border: '#fb923c40' },
    chat_message:         { bg: '#fbbf2420', text: '#fbbf24', border: '#fbbf2440' },
    command_use:          { bg: '#2dd4bf20', text: '#2dd4bf', border: '#2dd4bf40' },
    money_transaction:    { bg: '#34d39920', text: '#34d399', border: '#34d39940' },
    entity_create:        { bg: '#818cf820', text: '#818cf8', border: '#818cf840' },
    entity_delete:        { bg: '#6366f120', text: '#6366f1', border: '#6366f140' },
    admin_action:         { bg: '#e879f920', text: '#e879f9', border: '#e879f940' },
    anticheat_detection:  { bg: '#ff000030', text: '#ff4444', border: '#ff444460' },
    other:                { bg: '#94a3b820', text: '#94a3b8', border: '#94a3b840' }
};

const ACTION_LABELS = {
    player_connect: 'Kết nối', player_disconnect: 'Thoát',
    player_death: 'Chết', player_kill: 'Giết',
    vehicle_spawn: 'Cấp xe', vehicle_delete: 'Xóa xe',
    item_pickup: 'Thêm đồ', item_drop: 'Xóa đồ',
    weapon_fire: 'Bắn', chat_message: 'Chat',
    command_use: 'Lệnh', money_transaction: 'Tiền',
    entity_create: 'Tạo entity', entity_delete: 'Xóa entity',
    admin_action: 'Admin', anticheat_detection: '⚠️ Anticheat',
    other: 'Khác'
};

/**
 * Render give_item details: Người đưa → Người nhận
 */
const GiveItemDetail = ({ details }) => {
    if (!details?.giver && !details?.receiver) return null;
    const { giver, receiver, item } = details;
    return (
        <div className="give-item-detail">
            <div className="give-person giver">
                <span className="give-label">Người đưa</span>
                <span className="give-name">{giver?.name || '?'}</span>
                {giver?.id && <span className="give-id">ID: {giver.id}</span>}
                {giver?.pid && <span className="give-pid">PID: {giver.pid}</span>}
            </div>
            <FiArrowRight className="give-arrow" />
            <div className="give-person receiver">
                <span className="give-label">Người nhận</span>
                <span className="give-name">{receiver?.name || '?'}</span>
                {receiver?.id && <span className="give-id">ID: {receiver.id}</span>}
                {receiver?.pid && <span className="give-pid">PID: {receiver.pid}</span>}
            </div>
            {item && <div className="give-item"><span className="give-label">Vật phẩm</span><span className="give-item-name">{item}</span></div>}
        </div>
    );
};

/**
 * Render money transaction details
 */
const MoneyDetail = ({ details, title }) => {
    if (!details) return null;
    const isAdd = /cộng|thêm|nhận/i.test(title || '');
    return (
        <div className="money-detail">
            {details.playerName && <div className="money-row-detail"><span className="money-label">Nhân vật</span><span className="money-value">{details.playerName}</span></div>}
            {details.citizenId && <div className="money-row-detail"><span className="money-label">CitizenID</span><span className="money-value mono">{details.citizenId}</span></div>}
            {details.serverId && <div className="money-row-detail"><span className="money-label">Server ID</span><span className="money-value mono">{details.serverId}</span>{details.pid && <span className="money-pid">PID: {details.pid}</span>}</div>}
            {details.reason && <div className="money-row-detail"><span className="money-label">Lý do</span><span className="money-value">{details.reason}</span></div>}
            {details.amount && (
                <div className="money-row-detail">
                    <span className="money-label">Số tiền</span>
                    <span className={`money-amount ${isAdd ? 'add' : 'sub'}`}>
                        {isAdd ? '+' : '-'}${details.amount?.toLocaleString()} {details.currency || ''}
                    </span>
                </div>
            )}
            {details.balance && (
                <div className="money-row-detail">
                    <span className="money-label">Số dư</span>
                    <span className="money-value">${details.balance?.toLocaleString()} ({details.balanceType || 'cash'})</span>
                </div>
            )}
        </div>
    );
};

/**
 * Render leaderboard
 */
const LeaderboardDetail = ({ details }) => {
    if (!details?.leaderboard) return null;
    return (
        <div className="leaderboard-detail">
            {details.leaderboard.slice(0, 10).map(entry => (
                <div key={entry.rank} className="lb-row">
                    <span className={`lb-rank ${entry.rank <= 3 ? 'top' : ''}`}>#{entry.rank}</span>
                    <span className="lb-name">{entry.name}</span>
                    <span className="lb-cid">{entry.citizenId}</span>
                    <span className="lb-amount">${entry.amount?.toLocaleString()}</span>
                </div>
            ))}
            {details.leaderboard.length > 10 && <div className="lb-more">+{details.leaderboard.length - 10} người khác...</div>}
        </div>
    );
};

/**
 * Render embed card
 */
const EmbedCard = ({ embedData }) => {
    if (!embedData?.length) return null;
    const embed = embedData[0];
    if (!embed) return null;
    const title = embed.title || embed.data?.title;
    const description = embed.description || embed.data?.description;
    const fields = embed.fields || embed.data?.fields || [];
    const color = embed.color || embed.data?.color;
    const borderColor = color ? `#${color.toString(16).padStart(6, '0')}` : '#f0b429';

    return (
        <div className="embed-card" style={{ borderLeftColor: borderColor }}>
            {title && <div className="embed-title">{title}</div>}
            {description && <div className="embed-description">{description}</div>}
            {fields.length > 0 && (
                <div className="embed-fields">
                    {fields.map((f, i) => (
                        <div key={i} className="embed-field">
                            <span className="embed-field-name">{f.name}</span>
                            <span className="embed-field-value">{f.value}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Single log row
 */
const LogRow = ({ log }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);

    const hasDetails = (log.details && Object.keys(log.details).length > 0) || log.embedData?.length > 0;
    const colors = ACTION_COLORS[log.actionType] || ACTION_COLORS.other;
    const groupColor = log.channelGroupColor || '#6366f1';

    const formatTime = fmtTimeMultiline;

    const formatCoords = (c) => c ? `${c.x?.toFixed(0)}, ${c.y?.toFixed(0)}, ${c.z?.toFixed(0)}` : null;

    const isGiveItem = log.channelKey === 'give_item' || (log.details?.giver && log.details?.receiver);
    const isMoney = log.actionType === 'money_transaction';
    const isLeaderboard = log.details?.leaderboard;

    return (
        <>
            <tr className={`log-row ${expanded ? 'expanded' : ''} ${log.actionType === 'anticheat_detection' ? 'danger' : ''}`}
                onClick={() => hasDetails && setExpanded(!expanded)}>

                {/* Time */}
                <td className="log-time">
                    <div className="time-block">
                        {formatTime(log.timestamp).split('\n').map((t, i) => (
                            <span key={i} className={i === 0 ? 'time-main' : 'time-date'}>{t}</span>
                        ))}
                    </div>
                </td>

                {/* Channel */}
                <td className="log-channel">
                    <div className="channel-info">
                        <span className="channel-dot" style={{ backgroundColor: groupColor }}></span>
                        <div className="channel-text">
                            <span className="channel-name">#{log.channelLabel || log.channelKey}</span>
                            <span className="channel-group">{log.channelGroupLabel}</span>
                        </div>
                    </div>
                </td>

                {/* Player */}
                <td className="log-player">
                    {(() => {
                        if (isGiveItem && log.details?.giver) {
                            return (
                                <div className="player-give">
                                    <span className="player-name-small">{log.details.giver.name || '?'}</span>
                                    <FiArrowRight className="arrow-small" />
                                    <span className="player-name-small receiver">{log.details.receiver?.name || '?'}</span>
                                </div>
                            );
                        }
                        if (!log.playerName) return <span className="no-player">-</span>;

                        // Stable navigation target:
                        //   1) details.citizenId (gốc parser) — chính xác nhất
                        //   2) resolvedCitizenId (qua PlayerSession) — vẫn ổn định
                        //   3) playerSteamId nhưng CHỈ khi là steam: thật (license FiveM)
                        // Không bao giờ dùng "ID:N" — slot thoáng, sẽ click ra người khác.
                        const cid = log.details?.citizenId || log.resolvedCitizenId;
                        const steam = (typeof log.playerSteamId === 'string' && log.playerSteamId.startsWith('steam:'))
                            ? log.playerSteamId : null;
                        const navTarget = cid || steam || null;

                        const badge = cid
                            ? cid
                            : steam
                                ? steam
                                : (log.playerSteamId && /^ID:\d+$/i.test(log.playerSteamId) ? log.playerSteamId : null);

                        return (
                            <button
                                className={`player-link ${navTarget ? '' : 'no-target'}`}
                                disabled={!navTarget}
                                title={!navTarget ? 'Chưa resolve được người chơi ổn định' : ''}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (navTarget) navigate(`/player/${encodeURIComponent(navTarget)}`);
                                }}>
                                <FiUser className="icon" />
                                <span>{log.playerName}</span>
                                {badge && <span className="player-id-badge">{badge}</span>}
                                {!cid && log.playerSteamId && /^ID:\d+$/i.test(log.playerSteamId) && (
                                    <span className="player-id-warn" title="Slot ID — không đảm bảo là cùng người ở session khác">⚠</span>
                                )}
                            </button>
                        );
                    })()}
                </td>

                {/* Action Type */}
                <td className="log-action-type">
                    <span className="action-badge"
                        style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
                        {ACTION_LABELS[log.actionType] || log.actionType}
                    </span>
                </td>

                {/* Action Content */}
                <td className="log-action">
                    <span className="action-text" title={log.rawContent}>
                        {log.action || log.rawContent?.substring(0, 100)}
                    </span>
                </td>

                {/* Coords */}
                <td className="log-location">
                    {log.coordinates ? (
                        <span className="coords"><FiMapPin className="icon" />{formatCoords(log.coordinates)}</span>
                    ) : <span className="no-coords">-</span>}
                </td>

                {/* Expand */}
                <td className="log-expand">
                    {hasDetails && (
                        <button className="expand-btn" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
                            {expanded ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                    )}
                </td>
            </tr>

            {/* Expanded Detail Row */}
            {expanded && hasDetails && (
                <tr className="log-detail-row">
                    <td colSpan={7}>
                        <div className="log-detail-content">
                            {isGiveItem && <GiveItemDetail details={log.details} />}
                            {isMoney && !isLeaderboard && <MoneyDetail details={log.details} title={log.action} />}
                            {isLeaderboard && <LeaderboardDetail details={log.details} />}
                            {log.embedData?.length > 0 && !isGiveItem && !isMoney && !isLeaderboard && (
                                <EmbedCard embedData={log.embedData} />
                            )}
                            {!isGiveItem && !isMoney && !isLeaderboard && !log.embedData?.length && log.details && (
                                <div className="generic-details">
                                    {Object.entries(log.details).filter(([k, v]) => v && k !== 'amounts').map(([k, v]) => (
                                        <div key={k} className="detail-row">
                                            <span className="detail-key">{k}</span>
                                            <span className="detail-val">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// Group logs by player — each segment: { playerId, playerName, steamId, logs[] }
// Khóa nhóm = stable identifier (citizenId > steam: thật), KHÔNG dùng ID:N
// vì slot thoáng sẽ gộp nhầm nhiều người vào 1 segment.
const stableKey = (log) => {
    const cid = log.details?.citizenId || log.resolvedCitizenId;
    if (cid) return `cid:${cid}`;
    if (typeof log.playerSteamId === 'string' && log.playerSteamId.startsWith('steam:'))
        return log.playerSteamId;
    return log.playerName ? `name:${log.playerName}` : '__noone__';
};

const buildPlayerSegments = (logs) => {
    const sorted = [...logs].sort((a, b) => {
        const pA = stableKey(a);
        const pB = stableKey(b);
        if (pA !== pB) return pA.localeCompare(pB, 'vi');
        return new Date(a.timestamp) - new Date(b.timestamp);
    });

    const segments = [];
    let cur = null;
    sorted.forEach(log => {
        const pid = stableKey(log);
        if (!cur || cur.playerId !== pid) {
            cur = { playerId: pid, playerName: log.playerName, steamId: log.playerSteamId, logs: [] };
            segments.push(cur);
        }
        cur.logs.push(log);
    });
    return segments;
};

const LogTable = ({ logs, loading, onPageChange, pagination }) => {
    const [groupByPlayer, setGroupByPlayer] = useState(false);

    const segments = useMemo(
        () => groupByPlayer && logs ? buildPlayerSegments(logs) : null,
        [logs, groupByPlayer]
    );

    if (loading) {
        return (
            <div className="log-table-loading">
                <div className="loading-spinner"></div>
                <p>Đang tải logs...</p>
            </div>
        );
    }

    if (!logs || logs.length === 0) {
        return (
            <div className="log-table-empty">
                <p>Không có logs nào</p>
            </div>
        );
    }

    return (
        <div className="log-table-container">
            {/* Toolbar */}
            <div className="log-table-toolbar">
                <button
                    className={`group-player-btn ${groupByPlayer ? 'active' : ''}`}
                    onClick={() => setGroupByPlayer(v => !v)}
                    title="Gom log của cùng một người chơi lại cạnh nhau"
                >
                    <FiLayers />
                    {groupByPlayer ? 'Đang nhóm theo người chơi' : 'Nhóm theo người chơi'}
                </button>
                {groupByPlayer && segments && (
                    <span className="group-info">{segments.length} người chơi</span>
                )}
            </div>

          <div className="log-table-scroll-wrapper">
            <table className="log-table">
                <thead>
                    <tr>
                        <th style={{width:'90px'}}>Thời Gian</th>
                        <th style={{width:'160px'}}>Kênh</th>
                        <th style={{width:'200px'}}>Người Chơi</th>
                        <th style={{width:'110px'}}>Loại</th>
                        <th>Nội Dung</th>
                        <th style={{width:'120px'}}>Tọa Độ</th>
                        <th style={{width:'36px'}}></th>
                    </tr>
                </thead>
                <tbody>
                    {groupByPlayer && segments ? (
                        segments.map((seg, si) => (
                            <React.Fragment key={`seg-${si}-${seg.playerId}`}>
                                <tr className="player-group-header">
                                    <td colSpan={7}>
                                        <div className="pg-header-inner">
                                            <FiUser className="pg-icon" />
                                            <span className="pg-name">
                                                {seg.playerName || '— Không xác định —'}
                                            </span>
                                            {seg.steamId && (
                                                <span className="pg-id">{seg.steamId}</span>
                                            )}
                                            <span className="pg-count">{seg.logs.length} logs</span>
                                        </div>
                                    </td>
                                </tr>
                                {seg.logs.map((log) => (
                                    <LogRow key={log._id || log.discordMessageId} log={log} />
                                ))}
                            </React.Fragment>
                        ))
                    ) : (
                        logs.map((log) => (
                            <LogRow key={log._id || log.discordMessageId} log={log} />
                        ))
                    )}
                </tbody>
            </table>
          </div>

            {pagination && pagination.pages > 1 && (
                <div className="pagination">
                    <button className="page-btn" disabled={pagination.page <= 1}
                        onClick={() => onPageChange(pagination.page - 1)}>← Trước</button>
                    <div className="page-info">
                        Trang {pagination.page}/{pagination.pages}
                        <span className="total-count">({pagination.total?.toLocaleString()} logs)</span>
                    </div>
                    <button className="page-btn" disabled={pagination.page >= pagination.pages}
                        onClick={() => onPageChange(pagination.page + 1)}>Sau →</button>
                </div>
            )}
        </div>
    );
};

export default LogTable;
